import React, { useEffect, useRef, useState } from "react";
import { Box, Tabs, Tab, TextField, Typography, MenuItem, Button, Divider, Card, CardContent, IconButton, CircularProgress } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useForm, Controller } from "react-hook-form";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmLanguagesDialog } from "./confirm_languages_dialog";
import { addNotification, getSignedUrl } from "../../../services/notifications/notifications";
import { moduleTypes, computeDeepLink } from "../constants/notification-constants";
import MainLayout from "../../../layouts/main/main_layout";
import HTMLEditor from "../../../components/html_editor/html_editor";
import locales from "../../../constants/locales";
import SnackbarComp from "./snackbar";
import { MediaUpload } from "./media-upload";
import { useQuery } from "react-query";
import { getNeedsFilters } from "../../../services/needs/needs_service";
import { NeedFilters, Option } from "../../needs/model/needs_filters";
import { getServiceCategories } from "../../../services/services/services";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import useUser from "../../../providers/user/user_provider";
import { DEFAULT_ORG_ID } from "../../../constants";


export const AddNotification: React.FC<{ viewMode: boolean }> = ({ viewMode = false }) => {
    const location = useLocation();
    const notificationData = location.state?.notificationData || null;
    const isSchedulerFlow = !viewMode && !!location.state?.isScheduled;

    const exceptedModules = ['HOSPITAL', 'NEEDS', 'PROFILE','REFERRAL','HOMEPAGE', 'OTHERS'];

    const navigate = useNavigate();
    const { userPermissions } = useUser();
    const { selectedOrgId } = useSelectedOrgStore();
    const [tabIndex, setTabIndex] = useState(0);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [snackbar, setSnackbar] = useState<any>({
        open: false,
        message: "",
        severity: "success",
    });

    // For TTS: keep pending TTS audio Files per language (to upload on final submit)
    const [ttsFilesByLanguage, setTtsFilesByLanguage] = useState<Record<string, File>>({});
    // For Audio: keep pending audio Files per language (to upload on final submit)
    const [audioFilesByLanguage, setAudioFilesByLanguage] = useState<Record<string, File>>({});
    // For Video: keep pending video Files per language (to upload on final submit)
    const [videoFilesByLanguage, setVideoFilesByLanguage] = useState<Record<string, File>>({});
    const [ObjectId, setObjectId] = useState<string | null>(null);
    // Tracks the last value the auto-compute effect itself set, so it only
    // ever refreshes the Deep Link field while it still matches what it
    // last wrote there - an admin's manual edit is never silently clobbered.
    const autoComputedDeepLinkRef = useRef<string>("");
    // Separate image file for audio/TTS notifications (or standalone image)
    const [separateImageFile, setSeparateImageFile] = useState<FileList | undefined>(undefined);

    const [sharedData, setSharedData] = useState<SharedFormData>({
        mediaType: null,
        mediaFile: undefined,
        image: undefined,
        userFile: undefined,
        module: "",
        module_id: "",
        category_id: null,
        submodule_id: null,
        deep_link: "",
        isScheduled: isSchedulerFlow,
        scheduled_at: null,
    });

    const [formData, setFormData] = useState<Record<string, LanguageFormData>>(
        locales.reduce((acc, lang) => {
            acc[lang.code] = { title: "", description: "" };
            return acc;
        }, {} as Record<string, LanguageFormData>)
    );


    const { control, reset, watch } = useForm<LanguageFormData>({
        defaultValues: { title: "", description: "" }
    });

    const { control: sharedControl, reset: resetSharedForm, setValue: setSharedValue } = useForm<any>({
        defaultValues: {
            module: "",
            module_id: "",
            category_id: null,
            submodule_id: null,
            deep_link: "",
            mediaType: null,
            mediaFile: '',
            image: '',
            userFile: ''
        },
    });
    const { data: serviceCategories } = useQuery<any>(
        ["needs-filters"],
        async () => {
            const data=await getServiceCategories()
            console.log("data",data?.data?.data?.list);
            return data?.data?.data?.list
        }
    );

    const selectedCategoryId = sharedData?.category_id ?? null;
    const selectedSubcategoryId = sharedData?.submodule_id ?? null;
    const isCategorySelected = selectedCategoryId !== null && selectedCategoryId !== undefined;
    const isModuleIdSelected = !!sharedData?.module_id;

    function getSubCategories(){
        return (
            serviceCategories?.find((c:any) => c.id == selectedCategoryId)?.subcategories || []
        );
    }
    const currentLang = locales[tabIndex]['code'];


    const watchedTitle = watch("title");
    const watchedDescription = watch("description");



    // Handle media upload error
    const handleMediaUploadError = (message: string) => {
        setSnackbar({
            open: true,
            message: message,
            severity: "error",
        });
    };

    // Handle media type change
    const handleMediaTypeChange = (type: 'image' | 'tts' | 'audio' | 'video' | null | undefined) => {
        setSharedData((prev: any) => ({ 
            ...prev, 
            mediaType: type,
            mediaFile: undefined, // Clear previous file when changing type
            image: undefined // Clear image when changing type
        }));
        // Clear separate image file
        setSeparateImageFile(undefined);
        // Clear audio files when switching away from audio type
        if (type !== 'audio') {
            setAudioFilesByLanguage({});
        }
        // Clear video files when switching away from video type
        if (type !== 'video') {
            setVideoFilesByLanguage({});
        }
        // Clear TTS files when switching away from TTS type
        if (type !== 'tts') {
            setTtsFilesByLanguage({});
        }
    };

    // Handle media file change
    const handleMediaFileChange = (file: FileList | null) => {
        const mediaType = sharedData.mediaType || 'image';
        setSharedData((prev: any) => ({ 
            ...prev, 
            mediaFile: file || undefined,
            image: mediaType === 'image' ? file || undefined : prev.image // Keep image for backward compatibility
        }));
    };

    // Handle separate image file change (for audio/TTS)
    const handleImageFileChange = (file: FileList | null) => {
        setSeparateImageFile(file || undefined);
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const newLang = locales[newValue].code;
        setTabIndex(newValue);
        reset(formData[newLang]);

        // When viewing an existing notification, also switch media to match the selected language (for TTS per language)
        // Note: Image is global and should persist across language tabs
        if (viewMode && notificationData) {
            const contents = notificationData.contents || {};
            const contentForLang = contents[`content_${newLang}`] || {};

            // If mediaType is image, keep it (image is global, don't change when switching tabs)
            if (sharedData.mediaType === 'image') {
                // Keep image as-is, don't change it
                return;
            }

            // If this language has TTS audio, show that
            if (contentForLang.tts) {
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'tts',
                    mediaFile: contentForLang.tts,
                }));
            } else if (contentForLang.audio) {
                // If this language has uploaded audio, show that
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'audio',
                    mediaFile: contentForLang.audio,
                }));
            } else if (contentForLang.video) {
                // If this language has uploaded video, show that
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'video',
                    mediaFile: contentForLang.video,
                }));
            } else {
                // No TTS/Audio/Video for this language – keep existing mediaType/mediaFile (image) as-is
                // so non-TTS/Audio/Video media remains shared across languages.
            }
        }
    };

    const onSubmitAll = () => {
        setIsLoading(true);

        const isEmptyRichText = (html: string = ""): boolean => {
            const plainText = html.replace(/<[^>]+>/g, "").trim();
            return plainText === "";
        };

        if (!formData['en']?.title) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Title cannot be empty for english`,
                severity: "error",
            });
            return;
        }
        if (isEmptyRichText(formData['en']?.description)) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Description cannot be empty for english`,
                severity: "error",
            });
            return;
        }

        for (const lang of locales) {
            const title = formData[lang.code]?.title?.trim();
            const description = formData[lang.code]?.description;

            const hasTitle = !!title;
            const hasDescription = !isEmptyRichText(description);

            // Skip if both title and description are empty
            if (!hasTitle && !hasDescription) continue;

            if (!hasTitle) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title cannot be empty for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (title.length < 10) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title is too short for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (title.length > 60) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title is too long for ${lang.name}`,
                    severity: "error",
                });
                return;
            }

            if (!hasDescription) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description cannot be empty for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (description.length < 10) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description is too short for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (description.length > 1500) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description is too long for ${lang.name}`,
                    severity: "error",
                });
                return;
            }
        }

        if(sharedData.module=='SERVICE_CATEGORY' || sharedData.module=='SERVICE_SUBCATEGORY'){
            sharedData['module_id']=String(sharedData['category_id'])
            sharedData['submodule_id']=sharedData['submodule_id']
        }


        if (sharedData?.module && sharedData.module!='SERVICE_CATEGORY' && sharedData.module!='SERVICE_SUBCATEGORY' && !sharedData?.module_id ) {
            if (sharedData?.module && !exceptedModules.includes(sharedData?.module)) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please add deeplink ${sharedData?.module === 'EXTERNAL' ? ' URL' : ' ID'}`,
                    severity: "error",
                });
                return;
            }
        }

        if (sharedData?.module === 'OTHERS' && !sharedData?.deep_link?.trim()) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Please enter a Deep Link`,
                severity: "error",
            });
            return;
        }

        // If category is selected, sub-category is mandatory and module_id must not be used
        if (isCategorySelected && sharedData.module=='SERVICE_SUBCATEGORY' && !selectedSubcategoryId) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Please select a sub-category`,
                severity: "error",
            });
            return;
        }

        // Check if image file is selected when mediaType is 'image'
        // Image is global (not per language) and mandatory only when mediaType is 'image'
        if (sharedData?.mediaType === 'image' && currentLang === 'en') {
            const hasImageFile = sharedData.mediaFile && (
                (typeof sharedData.mediaFile === 'string') || 
                (typeof sharedData.mediaFile !== 'string' && sharedData.mediaFile[0])
            );
            
            if (!hasImageFile) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please select an image file. Image is mandatory for image type notifications.`,
                    severity: "error",
                });
                return;
            }
        }
        // For other media types (audio, video, tts), image is not mandatory

        if (!sharedData?.userFile?.[0]) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `No Excel file detected. Please attach the excel to proceed.`,
                severity: "error",
            });
            return;
        }

        if (sharedData?.isScheduled) {
            if (!sharedData?.scheduled_at) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please select a schedule date and time.`,
                    severity: "error",
                });
                return;
            }
            if (dayjs(sharedData.scheduled_at).isBefore(dayjs())) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Schedule time must be in the future.`,
                    severity: "error",
                });
                return;
            }
        }

        setIsLoading(false);
        setConfirmDialogOpen(true);
    };

    const handleFinalSubmit = async () => {
        const isEmptyRichText = (html: string = ""): boolean => {
            const plainText = html.replace(/<[^>]+>/g, "").trim();
            return plainText === "";
        }

        setIsLoading(true);
        try {

            // Always generate campaign ID if not already set (required for all notifications)
            let currentCampaignId: string | null = ObjectId;
            if (!currentCampaignId) {
                currentCampaignId = parseInt(
                    new Date().valueOf().toString() +
                    Math.floor(Math.random() * 100)
                        .toString()
                        .padEnd(2, "0"),
                ).toString();
                setObjectId(currentCampaignId);
            }

            // Helper function to get file from mediaFile
            const getFileFromMediaFile = (mediaFile: FileList | string | undefined): File | null => {
                if (!mediaFile) return null;
                if (typeof mediaFile === 'string') return null; // Already uploaded
                if (mediaFile instanceof FileList && mediaFile.length > 0) {
                    return mediaFile[0];
                }
                return null;
            };

            // Helper function to get content type from file
            const getContentType = (file: File): string => {
                if (file.type) return file.type;
                const extension = file.name.split('.').pop()?.toLowerCase();
                if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
                if (extension === 'png') return 'image/png';
                return 'image/jpeg';
            };

            // Upload main image file if mediaType is 'image'
            let uploadedMainImageFileName: string | null = null;
            if (sharedData?.mediaType === 'image') {
                const imageFile = getFileFromMediaFile(sharedData.mediaFile);
                if (imageFile) {
                    // File hasn't been uploaded yet, upload it now
                    const imageExt = imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
                    const imageFileName = `notification_image.${imageExt}`;
                    const contentType = getContentType(imageFile);

                    // Get signed URL for image
                    const signedUrlResponse: any = await getSignedUrl({
                        fileName: imageFileName,
                        bucketType: "notification",
                        contentType: contentType,
                        ObjectId: currentCampaignId,
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for image upload");
                    }

                    const fileBlob = await imageFile.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": contentType,
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload image file");
                    }

                    uploadedMainImageFileName = imageFileName;
                } else if (typeof sharedData.mediaFile === 'string') {
                    // Image is already uploaded (string URL), extract filename from URL
                    const urlParts = sharedData.mediaFile.split('/');
                    uploadedMainImageFileName = urlParts[urlParts.length - 1].split('?')[0];
                }
            }

            // Upload separate image file if present (for TTS notifications that need image in contents)
            let uploadedImageFileName: string | null = null;
            if (separateImageFile && separateImageFile[0]) {
                const imageFile = separateImageFile[0];
                const imageExt = imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
                const imageFileName = `notification_image.${imageExt}`;

                // Get signed URL for image
                const signedUrlResponse: any = await getSignedUrl({
                    fileName: imageFileName,
                    bucketType: "notification",
                    contentType: imageFile.type || "image/jpeg",
                    ObjectId: currentCampaignId,
                });

                const signedUrl =
                    signedUrlResponse?.data?.data?.signed_url ||
                    signedUrlResponse?.data?.data?.url ||
                    signedUrlResponse?.data?.signedUrl ||
                    signedUrlResponse?.data?.url;

                if (!signedUrl) {
                    throw new Error("Failed to get signed URL for image upload");
                }

                const fileBlob = await imageFile.arrayBuffer();
                const uploadResponse = await fetch(signedUrl, {
                    method: "PUT",
                    body: fileBlob,
                    headers: {
                        "Content-Type": imageFile.type || "image/jpeg",
                    },
                });

                if (!uploadResponse.ok) {
                    throw new Error("Failed to upload image file");
                }

                uploadedImageFileName = imageFileName;
            }

            // If media type is TTS, ensure we have generated audio for at least one language
            let ttsUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'tts') {
                if (!Object.keys(ttsFilesByLanguage).length) {
                    setIsLoading(false);
                    setSnackbar({
                        open: true,
                        message: "Please convert text to speech for at least one language before sending notification.",
                        severity: "error",
                    });
                    return;
                }
                

                // Upload all TTS files to bucket before sending notification
                // Use the already-generated currentCampaignId

                for (const [langCode, file] of Object.entries(ttsFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `tts_audio_${safeLang}.${ext}`;

                    // Get signed URL
                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "notification",
                        contentType: file.type || "audio/mpeg",
                        ObjectId: currentCampaignId,
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for TTS upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "audio/mpeg",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload TTS audio file");
                    }

                    // Store final file name per language
                    ttsUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            // If media type is Audio, ensure we have uploaded audio for at least one language
            let audioUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'audio') {
                if (!Object.keys(audioFilesByLanguage).length) {
                    setIsLoading(false);
                    setSnackbar({
                        open: true,
                        message: "Please upload an audio file for at least one language before sending notification.",
                        severity: "error",
                    });
                    return;
                }
                // Set uploading state to true to show loading
                setIsUploadingMedia(true);

                // Upload all audio files to bucket before sending notification
                for (const [langCode, file] of Object.entries(audioFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `audio_${safeLang}.${ext}`;

                    // Get signed URL
                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "notification",
                        contentType: file.type || "audio/mpeg",
                        ObjectId: currentCampaignId,
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for audio upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "audio/mpeg",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload audio file");
                    }

                    // Store final file name per language
                    audioUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            // If media type is Video, ensure we have uploaded video for at least one language
            let videoUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'video') {
                if (!Object.keys(videoFilesByLanguage).length) {
                    setIsLoading(false);
                    setSnackbar({
                        open: true,
                        message: "Please upload a video file for at least one language before sending notification.",
                        severity: "error",
                    });
                    return;
                }
                // Set uploading state to true to show loading
                setIsUploadingMedia(true);

                // Upload all video files to bucket before sending notification
                for (const [langCode, file] of Object.entries(videoFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `video_${safeLang}.${ext}`;

                    // Get signed URL
                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "notification",
                        contentType: file.type || "video/mp4",
                        ObjectId: currentCampaignId,
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for video upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "video/mp4",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload video file");
                    }

                    // Store final file name per language
                    videoUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            const contentPayload = locales.reduce((acc: any, lang: any) => {
                const langCode = lang.code;
                const { title, description } = formData[langCode] || {};
                const hasContent = title?.trim() || description?.trim() && (!isEmptyRichText(description) || !isEmptyRichText(title));

                if (hasContent) {
                    const contentForLang: any = { title, description };

                    // For TTS, attach per-language audio file (if uploaded) into contents
                    if (sharedData?.mediaType === 'tts') {
                        const audioFileNameForLang = ttsUploadedFileNamesByLang[langCode];
                        if (audioFileNameForLang) {
                            contentForLang.tts = audioFileNameForLang;
                        }
                    }
                    
                    // For Audio, attach per-language audio file (if uploaded) into contents
                    if (sharedData?.mediaType === 'audio') {
                        const audioFileNameForLang = audioUploadedFileNamesByLang[langCode];
                        if (audioFileNameForLang) {
                            contentForLang.media_url = audioFileNameForLang;
                        }
                    }
                    
                    // For Video, attach per-language video file (if uploaded) into contents
                    if (sharedData?.mediaType === 'video') {
                        const videoFileNameForLang = videoUploadedFileNamesByLang[langCode];
                        if (videoFileNameForLang) {
                            contentForLang.media_url = videoFileNameForLang;
                        }
                    }
                    acc[`content_${langCode}`] = contentForLang;
                }
                return acc;
            }, {});

            const formDataPayload = new FormData();
            
            // Determine media_type to send
            // If TTS is selected and image is uploaded, send media_type as "image"
            let mediaTypeToSend = sharedData?.mediaType;
            // if (sharedData?.mediaType === 'tts' && uploadedImageFileName) {
            //     mediaTypeToSend = 'image';
            // }
            
            if (mediaTypeToSend) {
                formDataPayload.append("media_type", mediaTypeToSend);
            }

            // Note: image is sent at top level
            if (sharedData?.mediaType === 'image') {
                formDataPayload.append("image_url", uploadedMainImageFileName || '');
            } else if (uploadedImageFileName) {
                // For audio/TTS notifications with separate image file
                formDataPayload.append("image_url", uploadedImageFileName);
            }

            // Always add campaign ID (required for all notifications)
            formDataPayload.append("campaign_id", currentCampaignId);

            if (!viewMode) {
                const targetOrgId = selectedOrgId ?? userPermissions?.orgId ?? DEFAULT_ORG_ID;
                formDataPayload.append("orgId", String(targetOrgId));
            }

            if (sharedData?.userFile?.[0]) {
                formDataPayload.append("userFile", sharedData.userFile[0]);
            }
            if (sharedData?.module) {
                formDataPayload.append("module", sharedData.module);
            }
            if (sharedData?.module_id && sharedData?.module!=='HOSPITAL' && sharedData?.module!=='NEEDS') {
                formDataPayload.append("module_id", sharedData.module_id);
            }
            if (sharedData?.category_id) {
                formDataPayload.append("category_id", String(sharedData.category_id));
            }
            if (sharedData?.submodule_id) {
                formDataPayload.append("submodule_id", String(sharedData.submodule_id));
            }
            if (sharedData?.deep_link?.trim()) {
                formDataPayload.append("deep_link", sharedData.deep_link.trim());
            }

            if (sharedData?.isScheduled && sharedData?.scheduled_at) {
                formDataPayload.append(
                    "scheduled_at",
                    dayjs(sharedData.scheduled_at).format("YYYY-MM-DDTHH:mm:ss+05:30"),
                );
            }

            formDataPayload.append("contents", JSON.stringify(contentPayload));
            setIsUploadingMedia(false);

            await addNotification(formDataPayload);
            setIsLoading(false);

            setSnackbar({
                open: true,
                message: "Notification added successfully!",
                severity: "success",
            });
            setConfirmDialogOpen(false);
            navigate(isSchedulerFlow ? "/notifications?currentTab=1" : "/notifications");
        } catch (error: any) {
            setIsLoading(false);
            setIsUploadingMedia(false);
            console.error("Submission failed:", error);
            const errorMsg = error?.response?.data?.error?.message || "Something went wrong.";
            setSnackbar({
                open: true,
                message: `${errorMsg}`,
                severity: "error",
            });
        }
    };

    const filledLanguages = locales.filter(
        (lang) => formData[lang.code]?.title.trim() && formData[lang.code]?.description.trim()
    ).map((lang) => lang.name);

    // Deep Link starts out bound to the selected Module + its id (so the
    // admin sees a correct default without typing anything), but stays
    // fully editable - once the admin types their own value, further
    // module_id/category_id changes stop overwriting it. SERVICE_CATEGORY/
    // SERVICE_SUBCATEGORY keep their id in category_id (not module_id, see
    // onSubmitAll's copy-on-submit), so that's what drives the default for
    // those two. Skipped in viewMode so an existing notification keeps
    // showing exactly what was saved, even if this mapping changes later.
    useEffect(() => {
        if (viewMode) return;
        const idValue = (sharedData.module === 'SERVICE_CATEGORY' || sharedData.module === 'SERVICE_SUBCATEGORY')
            ? sharedData.category_id
            : sharedData.module_id;
        const computed = computeDeepLink(sharedData.module, idValue);
        const current = sharedData.deep_link ?? "";
        if (current !== autoComputedDeepLinkRef.current) return; // admin edited it manually - leave it alone
        autoComputedDeepLinkRef.current = computed;
        if (current !== computed) {
            setSharedData(prev => ({ ...prev, deep_link: computed }));
            setSharedValue("deep_link", computed);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, sharedData.module, sharedData.module_id, sharedData.category_id]);

    useEffect(() => {
        if (viewMode) {
            reset(formData[currentLang]);
        }
    }, [tabIndex]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            [currentLang]: {
                title: watchedTitle,
                description: watchedDescription
            }
        }));
    }, [watchedTitle, watchedDescription, currentLang]);

    useEffect(() => {
        if (viewMode && notificationData) {
            const contents = notificationData.contents || {};
            const newFormData: Record<string, LanguageFormData> = {};
            const newUploadedAudioByLanguage: Record<string, string> = {};
            const newUploadedVideoByLanguage: Record<string, string> = {};
            const newUploadedTtsByLanguage: Record<string, string> = {};

            Object.entries(contents).forEach(([key, value]: any) => {
                const langCode = key.replace("content_", "");
                newFormData[langCode] = {
                    title: value?.title || "",
                    description: value?.description || ""
                };

                // If TTS audio URL is present in contents, store it per language
                if (value?.tts) {
                    newUploadedTtsByLanguage[langCode] = value.tts;
                }
                
                // If uploaded audio URL is present in contents, store it per language
                if (value?.audio) {
                    newUploadedAudioByLanguage[langCode] = value.audio;
                }
                
                // If uploaded video URL is present in contents, store it per language
                if (value?.video) {
                    newUploadedVideoByLanguage[langCode] = value.video;
                }
                
                // If media_url is present (could be audio or video), check media type
                if (value?.media_url) {
                    // Determine if it's audio or video based on file extension or media type
                    const mediaUrl = value.media_url;
                    if (mediaUrl.includes('audio_') || mediaUrl.match(/\.(mp3|wav|ogg)$/i)) {
                        newUploadedAudioByLanguage[langCode] = mediaUrl;
                    } else if (mediaUrl.includes('video_') || mediaUrl.match(/\.(mp4|webm|ogg)$/i)) {
                        newUploadedVideoByLanguage[langCode] = mediaUrl;
                    }
                }
            });

            // Determine media type and file URL from notification data
            // Check for both camelCase (mediaType) and snake_case (media_type)
            let mediaType: 'image' | 'tts' | 'audio' | 'video' | undefined | null = 
                notificationData.mediaType || notificationData.media_type;
            
            // If media type is not explicitly set, infer from URLs
            if (!mediaType) {
                if (notificationData.image_url) {
                    mediaType = 'image';
                }
            }
            
            // Prefer per-language TTS, Audio, or Video (if present) when viewing
            let mediaFileUrl: string | undefined;
            const ttsForCurrentLang = newUploadedTtsByLanguage[currentLang];
            const audioForCurrentLang = newUploadedAudioByLanguage[currentLang];
            const videoForCurrentLang = newUploadedVideoByLanguage[currentLang];
            if (ttsForCurrentLang) {
                mediaType = 'tts';
                mediaFileUrl = ttsForCurrentLang;
            } else if (audioForCurrentLang) {
                mediaType = 'audio';
                mediaFileUrl = audioForCurrentLang;
            } else if (videoForCurrentLang) {
                mediaType = 'video';
                mediaFileUrl = videoForCurrentLang;
            } else {
                // Get the appropriate media URL based on media type
                mediaFileUrl = mediaType === 'image' ? notificationData.image_url : undefined;
            }            

            setFormData(newFormData);            
            setSharedData({
                module: notificationData.module,
                module_id: notificationData.module_id || notificationData.category_id,
                category_id: notificationData.module_id  ?? null,
                submodule_id: notificationData.submodule_id ?? null,
                deep_link: notificationData.deep_link || "",
                mediaType: mediaType, // Explicitly set the detected media type (may be 'tts' if coming from contents)
                mediaFile: mediaFileUrl || undefined,
                image: notificationData.image_url || undefined,         // string URL (backward compatibility)
                userFile: notificationData.user_file_url || undefined,  // string URL
                isScheduled: !!notificationData.is_scheduled,
                scheduled_at: notificationData.scheduled_at || null,
            });

            reset(newFormData[currentLang]);
            resetSharedForm({
                module: notificationData.module,
                module_id: notificationData.module_id,
                category_id: (notificationData.category_id || notificationData.module_id) ?? null,
                submodule_id: notificationData.submodule_id ?? null,
                deep_link: notificationData.deep_link || "",
                mediaType: mediaType || null
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, notificationData, currentLang]);



    return (
        <MainLayout>
            <Card sx={{ width: "100%", borderRadius: 2, m: 1 }} elevation={1}>
                <CardContent>
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                        <Box width={400} display="flex" alignItems="center">
                            <IconButton onClick={() => navigate(-1)} disabled={isLoading || isUploadingMedia}>
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h6" fontWeight="600" paddingLeft={1}>
                                {viewMode ? "View Notification" : isSchedulerFlow ? "Schedule Notification" : "Add Notification"}
                            </Typography>
                        </Box>
                        {!viewMode && (
                            <Box minWidth={240}>
                                <OrgSelector lockToOwnOrg platformDefaultsToOwnOrg fullWidth />
                            </Box>
                        )}
                    </Box>

                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        {locales.map((locale) => (
                            <Tab key={locale.code} label={locale.name} />
                        ))}
                    </Tabs>

                    <Divider sx={{ mb: 2 }} />

                    <Controller
                        name="title"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={currentLang === 'en' ? <>Title <span style={{ color: 'red' }}>*</span></> : 'Title'}
                                margin="normal"
                                disabled={viewMode}
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <HTMLEditor
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={`Enter Description ${currentLang === 'en' ? '*' : ''}`}
                                readOnly={viewMode}
                            />
                        )}
                    />

                    {currentLang === "en" && (
                        <>
                            {(!viewMode || (viewMode && sharedData?.module)) && <Controller
                                name="module"
                                control={sharedControl}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        label="Select Module Type"
                                        fullWidth
                                        margin="normal"
                                        {...field}
                                        disabled={viewMode}
                                        onChange={e => {
                                            field.onChange(e);
                                            autoComputedDeepLinkRef.current = "";
                                            setSharedData(prev => ({
                                                ...prev,
                                                module: e.target.value,
                                                module_id: "",
                                                category_id: null,
                                                submodule_id: null,
                                                deep_link: "",
                                            }));
                                            setSharedValue("module_id", "");
                                            setSharedValue("category_id", null);
                                            setSharedValue("submodule_id", null);
                                            setSharedValue("deep_link", "");
                                        }}
                                    >
                                        {moduleTypes.map(mod => (
                                            <MenuItem key={mod.key} value={mod.value}>
                                                {mod.key}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />}

                            { (sharedData.module!='SERVICE_CATEGORY' && sharedData.module!='SERVICE_SUBCATEGORY' ) && (!viewMode || (viewMode && sharedData?.module)) && (!exceptedModules.includes(sharedData?.module || '')) && <Controller
                                name="module_id"
                                control={sharedControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={
                                            sharedData.module === "EXTERNAL"
                                                ? "Deeplink URL"
                                                : sharedData.module === "JOB"
                                                    ? "Category ID"
                                                    : "Module ID"
                                        }

                                        margin="normal"
                                        disabled={viewMode || isCategorySelected}
                                        onChange={e => {
                                            field.onChange(e);
                                            const nextVal = e.target.value;
                                            setSharedData(prev => ({
                                                ...prev,
                                                module_id: nextVal,
                                                category_id: nextVal ? null : prev.category_id ?? null,
                                                submodule_id: nextVal ? null : prev.submodule_id ?? null,
                                            }));
                                            if (nextVal) {
                                                setSharedValue("category_id", null);
                                                setSharedValue("submodule_id", null);
                                            }
                                        }}
                                    />
                                )}
                            />}

                            {((sharedData.module=='SERVICE_CATEGORY' || sharedData.module=='SERVICE_SUBCATEGORY' )) && (
                                <>
                                    <Controller
                                        name="category_id"
                                        control={sharedControl}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="Category"
                                                fullWidth
                                                margin="normal"
                                                {...field}
                                                value={field.value ?? ""}
                                                disabled={viewMode || isModuleIdSelected}
                                                onChange={(e) => {
                                                    const val = e.target.value === "" ? null : Number(e.target.value);
                                                    field.onChange(val);
                                                    setSharedData((prev) => ({
                                                        ...prev,
                                                        category_id: val,
                                                        submodule_id: null,
                                                        module_id: val ? "" : prev.module_id,
                                                    }));
                                                    setSharedValue("submodule_id", null);
                                                    if (val) {
                                                        setSharedValue("module_id", "");
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">Select Category</MenuItem>
                                                {(serviceCategories || []).map((c:any) => (
                                                    <MenuItem key={c.id} value={c.id}>
                                                        {c.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />

                                    {sharedData.module=='SERVICE_SUBCATEGORY' && <Controller
                                        name="submodule_id"
                                        control={sharedControl}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label={isCategorySelected ? <>Sub-category <span style={{ color: 'red' }}>*</span></> : "Sub-category"}
                                                fullWidth
                                                margin="normal"
                                                {...field}
                                                value={field.value ?? ""}
                                                disabled={viewMode || isModuleIdSelected || !isCategorySelected}
                                                onChange={(e) => {
                                                    const val = e.target.value === "" ? null : Number(e.target.value);
                                                    field.onChange(val);
                                                    setSharedData((prev) => ({ ...prev, submodule_id: val }));
                                                }}
                                            >
                                                <MenuItem value="">
                                                    {isCategorySelected ? "Select Sub-category" : "Select Category First"}
                                                </MenuItem>
                                                {getSubCategories().map((s:any) => (
                                                    <MenuItem key={s.id} value={s.id}>
                                                        {s.name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />}
                                </>
                            )}

                            {(!viewMode || (viewMode && sharedData?.deep_link)) && (
                                <Controller
                                    name="deep_link"
                                    control={sharedControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Deep Link"
                                            helperText="The above link is used in the mobile app. If you want to edit the deeplink, you can edit it."
                                            margin="normal"
                                            disabled={viewMode}
                                            onChange={e => {
                                                field.onChange(e);
                                                setSharedData(prev => ({ ...prev, deep_link: e.target.value }));
                                            }}
                                        />
                                    )}
                                />
                            )}
                        </>
                    )}


                    <MediaUpload
                        tabIndex={tabIndex}
                        viewMode={viewMode}
                        mediaType={sharedData.mediaType || null}
                        setSharedData={setSharedData}
                        mediaFile={sharedData.mediaFile}
                        control={sharedControl}
                        title={formData[currentLang]?.title || ''}
                        language={currentLang}
                        onMediaTypeChange={handleMediaTypeChange}
                        onMediaFileChange={handleMediaFileChange}
                        onUploadSuccess={() => {}}
                        onError={handleMediaUploadError}
                        onTtsGenerated={(langCode, file) => {
                            setTtsFilesByLanguage(prev => ({
                                ...prev,
                                [langCode]: file,
                            }));
                        }}
                        onAudioFileChange={(langCode, file) => {
                            setAudioFilesByLanguage(prev => ({
                                ...prev,
                                [langCode]: file,
                            }));
                        }}
                        onVideoFileChange={(langCode, file) => {
                            setVideoFilesByLanguage(prev => ({
                                ...prev,
                                [langCode]: file,
                            }));
                        }}
                        onImageFileChange={handleImageFileChange}
                        imageFile={viewMode && sharedData.image ? sharedData.image : separateImageFile}
                    />

                    {/* Backward compatibility: Show old image field if it exists and mediaType is not set and mediaFile is not available */}
                    {viewMode && sharedData.image && !sharedData.mediaType && !sharedData.mediaFile && (
                            <Box mt={2}>
                                <Typography variant="subtitle2">Uploaded Image</Typography>
                                <Box mt={1}>
                                    <img
                                        src={typeof sharedData.image === 'string' ? sharedData.image : URL.createObjectURL(sharedData.image[0])}
                                        alt="Uploaded"
                                        style={{ width: 300, height: 300, objectFit: 'contain', borderRadius: 8 }}
                                    />
                                </Box>
                            </Box>
                    )}




                    {currentLang === 'en' && (
                        viewMode && sharedData.userFile ? (
                            <Box mt={3}>
                                <Typography variant="subtitle2">Uploaded Excel File</Typography>
                                <Box mt={1}>
                                    <Button
                                        variant="outlined"
                                        component="a"
                                        href={typeof sharedData.userFile === 'string'
                                            ? sharedData.userFile
                                            : URL.createObjectURL(sharedData.userFile[0])}
                                        download
                                    >
                                        Download User Excel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Controller
                                name="userFile"
                                control={sharedControl}
                                render={({ field }) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Button variant="outlined" component="label" sx={{ mt: 2 }}>
                                            Upload Users Excel
                                            <input
                                                type="file"
                                                accept=".xlsx,.xls"
                                                hidden
                                                onChange={e => {
                                                    field.onChange(e.target.files);
                                                    setSharedData((prev: any) => ({ ...prev, userFile: e.target.files }));
                                                }}
                                            />
                                        </Button>
                                        <span style={{ color: 'red' }}>*</span>
                                        {sharedData.userFile?.[0]?.name && (
                                            <div style={{ marginTop: '16px' }}>
                                                {sharedData.userFile[0].name}
                                            </div>
                                        )}
                                    </div>
                                )}
                            />
                        )
                    )}

                    {currentLang === 'en' && sharedData?.isScheduled && (
                        <Box mt={2}>
                            <DateTimePicker
                                label="Schedule Date & Time"
                                disablePast
                                readOnly={viewMode}
                                closeOnSelect={false}
                                minTime={
                                    sharedData?.scheduled_at && dayjs(sharedData.scheduled_at).isSame(dayjs(), "day")
                                        ? dayjs()
                                        : undefined
                                }
                                shouldDisableTime={(value, view) => {
                                    const isToday = sharedData?.scheduled_at
                                        ? dayjs(sharedData.scheduled_at).isSame(dayjs(), "day")
                                        : false;
                                    if (!isToday) return false;
                                    if (view === "hours") {
                                        return value.isBefore(dayjs(), "hour");
                                    }
                                    if (view === "minutes") {
                                        return value.isBefore(dayjs(), "minute");
                                    }
                                    return false;
                                }}
                                value={sharedData?.scheduled_at ? dayjs(sharedData.scheduled_at) : null}
                                onChange={(value: Dayjs | null) => {
                                    setSharedData((prev) => ({
                                        ...prev,
                                        scheduled_at: value ? value.toISOString() : null,
                                    }));
                                }}
                                onAccept={(value: Dayjs | null) => {
                                    if (value && value.isBefore(dayjs())) {
                                        setSnackbar({
                                            open: true,
                                            message: "Schedule time must be in the future.",
                                            severity: "error",
                                        });
                                        setSharedData((prev) => ({
                                            ...prev,
                                            scheduled_at: null,
                                        }));
                                    }
                                }}
                                slotProps={{
                                    desktopPaper: { elevation: 3 },
                                    textField: { sx: { mt: 1 } },
                                    actionBar: { actions: ["cancel", "accept"] },
                                }}
                                sx={{ "& .MuiSvgIcon-root": { fill: "#990099" } }}
                            />
                        </Box>
                    )}

                    {!viewMode && currentLang === 'en' && (
                        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 2 }}>
                            {isUploadingMedia && (
                                <Typography variant="body2" color="text.secondary">
                                    Uploading media...
                                </Typography>
                            )}
                            <Button
                                disabled={isLoading || isUploadingMedia}
                                onClick={onSubmitAll}
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2 }}
                                startIcon={(isLoading || isUploadingMedia) ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isUploadingMedia ? 'Uploading Media...' : isLoading ? 'Sending...' : (isSchedulerFlow ? 'Schedule Notification' : 'Send Notifications')}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>

            <ConfirmLanguagesDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleFinalSubmit}
                filledLanguages={filledLanguages}
                isLoading={isLoading || isUploadingMedia}
            />

            <SnackbarComp
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </MainLayout>
    );
};
